/**
 * Icon-font class → friendly label resolution.
 *
 * Many component frameworks render icon-only buttons whose only semantic
 * signal lives on a child icon element's class, e.g. FontAwesome
 * (`<i class="fa-solid fa-file-export">`), Telerik/Kendo
 * (`<svg class="k-svg-i-filter">`), or generic `icon-<name>` conventions.
 * Such buttons reach the accessibility tree with an empty name; without this
 * mapping the augmenter falls back to a framework marker class (e.g.
 * `telerik-blazor`) and every toolbar button collapses to the same useless
 * label.
 *
 * This module strips the icon-family/style prefixes off a raw icon class
 * token and maps the remaining icon name to a human verb/noun the agent can
 * reason over. Unknown names fall back to title-cased English so even
 * un-mapped icons get a usable label rather than a framework marker.
 *
 * Sibling of {@link module:lucide-icon-names} — lucide has its own module
 * because lucide names arrive pre-extracted from the SVG `class`; this module
 * handles the broader icon-font families.
 *
 * @module
 */

/**
 * Icon-family / style prefixes that carry no meaning on their own and must be
 * stripped before mapping. Order-independent — every matching leading prefix
 * is removed. Kept deliberately small: only well-established icon systems.
 */
const ICON_FAMILY_PREFIXES = [
  // FontAwesome family + style tokens
  'fa-solid-',
  'fa-regular-',
  'fa-light-',
  'fa-thin-',
  'fa-duotone-',
  'fa-brands-',
  'fa-sharp-',
  'fa-',
  'fas-',
  'far-',
  'fal-',
  'fad-',
  'fab-',
  // Telerik / Kendo
  'k-svg-i-',
  'k-i-',
  // Bootstrap Icons, Material Design Icons, Glyphicons, Ionicons
  'bi-',
  'mdi-',
  'glyphicon-',
  'ion-',
  'material-icons-',
  // Generic convention
  'icon-',
];

/**
 * Decorative / sizing tokens that may ride along on a FontAwesome element but
 * are never the icon name. Used to reject them as candidate tokens upstream
 * and, defensively, to title-case nothing here.
 */
export const ICON_NOISE_TOKENS = new Set([
  'fa',
  'fa-solid',
  'fa-regular',
  'fa-light',
  'fa-thin',
  'fa-duotone',
  'fa-brands',
  'fa-sharp',
  'fas',
  'far',
  'fal',
  'fad',
  'fab',
  'fa-lg',
  'fa-sm',
  'fa-xs',
  'fa-xl',
  'fa-fw',
  'fa-spin',
  'fa-pulse',
  'fa-border',
  'fa-pull-left',
  'fa-pull-right',
  'k-icon',
  'k-svg-icon',
  'k-button-icon',
]);

/**
 * Curated mapping of icon names (after family/style prefix stripping) to UI
 * verbs / nouns. Grow on demand — only names observed in real captures should
 * land here. Keys are the bare, lowercased icon name.
 */
export const ICON_NAME_MAP: Record<string, string> = {
  // export / data movement
  'file-export': 'Export',
  'file-arrow-down': 'Export',
  'arrow-up-from-bracket': 'Export',
  export: 'Export',
  download: 'Download',
  'file-download': 'Download',
  'cloud-arrow-down': 'Download',
  'arrow-down-to-line': 'Download',
  upload: 'Upload',
  'file-upload': 'Upload',
  'cloud-arrow-up': 'Upload',
  'file-import': 'Import',
  'file-arrow-up': 'Import',
  import: 'Import',
  // filtering / search / columns
  filter: 'Filter',
  'filter-circle-xmark': 'Clear filter',
  funnel: 'Filter',
  sliders: 'Filter',
  'magnifying-glass': 'Search',
  search: 'Search',
  'table-columns': 'Columns',
  columns: 'Columns',
  'table-cells': 'Columns',
  table: 'Columns',
  // settings
  gear: 'Settings',
  gears: 'Settings',
  cog: 'Settings',
  cogs: 'Settings',
  wrench: 'Settings',
  sliders2: 'Settings',
  // CRUD
  plus: 'Add',
  'plus-circle': 'Add',
  'circle-plus': 'Add',
  add: 'Add',
  pen: 'Edit',
  pencil: 'Edit',
  'pen-to-square': 'Edit',
  'pencil-alt': 'Edit',
  edit: 'Edit',
  trash: 'Delete',
  'trash-can': 'Delete',
  'trash-alt': 'Delete',
  delete: 'Delete',
  'floppy-disk': 'Save',
  save: 'Save',
  copy: 'Copy',
  clone: 'Copy',
  // navigation / disclosure
  xmark: 'Close',
  times: 'Close',
  x: 'Close',
  'circle-xmark': 'Close',
  close: 'Close',
  'chevron-down': 'Expand',
  'chevron-up': 'Collapse',
  'chevron-left': 'Previous',
  'chevron-right': 'Next',
  'angle-down': 'Expand',
  'angle-up': 'Collapse',
  'angle-left': 'Previous',
  'angle-right': 'Next',
  'caret-down': 'Expand',
  'caret-up': 'Collapse',
  'caret-left': 'Previous',
  'caret-right': 'Next',
  'arrow-left': 'Back',
  'arrow-right': 'Forward',
  // misc actions
  ellipsis: 'More',
  'ellipsis-vertical': 'More',
  'ellipsis-v': 'More',
  'ellipsis-h': 'More',
  bars: 'Menu',
  bell: 'Notifications',
  'circle-info': 'Info',
  'info-circle': 'Info',
  info: 'Info',
  'circle-question': 'Help',
  'question-circle': 'Help',
  question: 'Help',
  check: 'Confirm',
  'circle-check': 'Confirm',
  eye: 'Show',
  'eye-slash': 'Hide',
  'arrows-rotate': 'Refresh',
  'arrow-rotate-right': 'Refresh',
  rotate: 'Refresh',
  refresh: 'Refresh',
  sync: 'Refresh',
  print: 'Print',
  printer: 'Print',
  share: 'Share',
  'share-nodes': 'Share',
  calendar: 'Calendar',
  'calendar-days': 'Calendar',
  star: 'Favorite',
  print2: 'Print',
};

/**
 * Strip leading icon-family/style prefixes off a raw icon class token,
 * returning the bare lowercased icon name (or `undefined` when nothing
 * meaningful remains).
 */
export function stripIconFamilyPrefix(raw: string): string | undefined {
  let name = raw.trim().toLowerCase();
  if (!name) return undefined;
  // Repeatedly strip a matching family prefix (handles `fa-solid-` style
  // double prefixes that may have been joined upstream).
  let changed = true;
  while (changed) {
    changed = false;
    for (const prefix of ICON_FAMILY_PREFIXES) {
      if (name.startsWith(prefix) && name.length > prefix.length) {
        name = name.slice(prefix.length);
        changed = true;
        break;
      }
    }
  }
  if (!name || ICON_NOISE_TOKENS.has(name) || ICON_NOISE_TOKENS.has(raw.trim().toLowerCase())) {
    return undefined;
  }
  return name;
}

/**
 * Resolve a raw icon class token (e.g. `fa-solid fa-file-export`'s
 * `fa-file-export`, or `k-svg-i-filter`) to a friendly UI label. Falls back to
 * title-cased English when the icon isn't in the curated map. Returns
 * `undefined` when the token is pure family/sizing noise.
 */
export function resolveIconClassName(raw: string): string | undefined {
  const name = stripIconFamilyPrefix(raw);
  if (!name) return undefined;
  const mapped = ICON_NAME_MAP[name];
  if (mapped) return mapped;
  // Drop a trailing numeric/style discriminator like `edit-2` → `edit` before
  // giving up, so common variant names still map.
  const base = name.replace(/-\d+$/, '');
  if (base !== name && ICON_NAME_MAP[base]) return ICON_NAME_MAP[base];
  return name
    .split('-')
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ');
}
